import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, AlertCircle } from 'lucide-react';
import Card from './Card';
import Button from './Button';
import Spinner from './Spinner';
import syncService from '../../services/syncService';

// MISSING MAPPINGS — Stay History Sync.
//
// Every code value this project's stay records went to OPERA Cloud with as-is: no STAY conversion for it and
// no STAY default for its field. Stay failures are expected to be mostly these (a retired rate code, an old
// market segment), so this is the list to work from: add a conversion (record type STAY) or a default for the
// field, then reprocess the failed stays from Reconciliation — they are re-sent from the stored message with
// the new mapping applied — and run Verify Stay Records.
//
// Read-only on purpose: conversions and defaults are edited where they always are, so there is one editor.

const FIELD_LABELS = {
  RATECODE: 'Rate Code', MARKETCODE: 'Market Code', SOURCECODE: 'Source Code', ROOMTYPE: 'Room Type', CHANNEL: 'Channel',
};

const StayUnmappedCodesPanel = ({ syncId, showToast }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ codes: [], records: 0 });
  // Held in a ref: the task page passes a new showToast on every render (see ConversionCodesPanel).
  const toastRef = useRef(showToast);
  useEffect(() => { toastRef.current = showToast; });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await syncService.getStayUnmappedCodes(syncId));
    } catch (e) {
      if (toastRef.current) toastRef.current(`Could not load the missing mappings: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [syncId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <div className="card-header">
        <h2>Missing Mappings</h2>
        <div className="header-actions">
          <Button variant="secondary" onClick={load} disabled={loading}>
            <RefreshCw className={`icon-sm ${loading ? 'spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>
      <p className="form-hint" style={{ margin: '0 0 0.75rem' }}>
        Code values stay records were sent with unchanged, because there is no STAY conversion and no default for
        them. Add a conversion (record type STAY) or a default for the field, reprocess the failed stays from
        Reconciliation, then run Verify Stay Records.
      </p>
      {loading ? (
        <div className="loading-section"><Spinner /></div>
      ) : data.codes.length === 0 ? (
        <div className="empty-state">
          <AlertCircle className="empty-icon" />
          <p>No unmapped values — every stay code sent so far had a conversion or a default.</p>
        </div>
      ) : (
        <div className="conversions-table-wrapper">
          <p className="form-hint" style={{ margin: '0 0 0.5rem' }}>
            {data.codes.length} value(s) across {data.records} stay record(s).
          </p>
          <table className="conversions-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Value</th>
                <th>Stay records</th>
                <th>Of which in error</th>
              </tr>
            </thead>
            <tbody>
              {data.codes.map((c) => (
                <tr key={`${c.datatype}:${c.value}`}>
                  <td>{FIELD_LABELS[c.datatype] || c.datatype}</td>
                  <td><code>{c.value}</code></td>
                  <td>{c.records}</td>
                  <td>{c.errorRecords}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default StayUnmappedCodesPanel;
